/** ONE formatting vocabulary for the whole UI (U4). Every page renders time,
 *  counts, and bytes through these — never a local copy (UI law: check lib/
 *  first; a second hand-built copy is a review failure). */

/** A DURATION in seconds as "32s" / "5m" / "3h" / "2d".
 *
 *  Extracted 2026-10-18 because this ladder was already written twice — in
 *  `ago` and in `until` — and a third caller (the record table's arrival-lag
 *  tooltip) would have made three. The two existing copies had already
 *  drifted once in spirit: `ago` clamps at zero and `until` returns "now",
 *  which is a real difference in MEANING and stays in those functions, while
 *  the formatting itself has no business being duplicated. */
export function dur(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

/** Relative age: "32s" / "5m" / "3h" / "2d". Dash for null. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dur(Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000));
}

/** How long UNTIL an instant in the future: "23h", "4m", or "now" once it has
 *  passed. The mirror of `ago`, and it exists because `ago` is deliberately
 *  clamped at zero (`Math.max(0, …)`) for a past-tense reading — so rendering
 *  a FUTURE deadline through it printed "in 0s" on a command with 24 hours
 *  left to run, which Dattatray read on the console as a command about to
 *  expire. Two directions, two functions. */
export function until(iso: string | null | undefined): string {
  if (!iso) return "—";
  const s = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  if (s <= 0) return "now";
  return dur(s);
}

/** Date + wall-clock with ms: "09 Aug 16:21:24.598" — table timestamps must
 *  carry the DATE (a history window can span days; time alone is ambiguous). */
export function dateTime(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString([], { day: "2-digit", month: "short" });
  return `${day} ${hhmmss(iso)}`;
}

/** `2026-10-20 00:47:34.598` — the browser's LOCAL clock, in a shape a
 *  spreadsheet parses as a date-time.
 *
 *  FOR EXPORTS, where `dateTime()` is for the screen. Dattatray: *"The timing
 *  shall be the laptop local time (same way we show for live data time)."*
 *  The CSV was writing the raw ISO string straight from the API — UTC, +00:00
 *  suffix and all — so a row the console showed at 00:47 read 19:17 in Excel
 *  and nobody could line an export up against what they had just watched.
 *
 *  `dateTime()` cannot be reused for this: "19 Oct 00:47:34.598" has no year
 *  and no numeric month, and Excel imports it as text. Same clock as the
 *  screen (every getter below is a local-time getter, which is the whole
 *  conversion), different shape, so both live in this one file rather than a
 *  hand-rolled conversion at the call site.
 *
 *  The caller must say WHICH zone — `tzName()` below — because a bare local
 *  stamp in a file that outlives the session is unreadable otherwise. */
export function localStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + ` ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    + `.${p(d.getMilliseconds(), 3)}`;
}

/** The IANA zone the browser is in ("Asia/Kolkata"), for labelling an export's
 *  time columns. Falls back to the UTC offset where Intl is unavailable. */
export function tzName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
      || `UTC${-new Date().getTimezoneOffset() / 60 >= 0 ? "+" : ""}${-new Date().getTimezoneOffset() / 60}`;
  } catch {
    return "local time";
  }
}

/** Wall-clock with milliseconds: "16:21:24.598" (console/table timestamps). */
export function hhmmss(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
}

/** Counts auto-scale K / M / B; exact value belongs in a tooltip. */
export function fmtCount(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 10_000) return `${(v / 1e3).toFixed(1)}K`;
  return v.toLocaleString("en-IN");
}

/** Bytes auto-scale B / KB / MB / GB. */
export function fmtBytes(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(2)} GB`;
  if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v >= 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${v} B`;
}

/** Grouped integer (en-IN, the app's convention). */
export function fmtInt(v: number | null | undefined): string {
  return v == null ? "—" : v.toLocaleString("en-IN");
}

/** Poll cadence budget (UI performance law 1) — pick the slowest tier that
 *  matches how fast the DATA actually changes, never a faster one. */
export const POLL = {
  /** live consoles / tails — seconds-fresh views */
  CONSOLE: 2000,
  /** operational state (rollout progress, connections) */
  STATE: 5000,
  /** dashboards, aggregates, distributions */
  DASHBOARD: 15000,
  /** registry/config lists — change on human timescales */
  LIST: 15000,
} as const;
